import type { QueryClient } from '@tanstack/react-query';
import type { UIMessage } from 'ai';
import { chatKeys } from '~/lib/query-keys';
import type { ApproveResponse } from '~/hooks/mutations/use-approval-actions';
import { LATE_APPROVAL_CONTINUATION, readSourceMeta } from './source-chip';

/**
 * FRONT-3036: an open tab picks up the turn the SERVER starts after a late
 * approval settles.
 *
 * The tab sends nothing any more, and `use-sse.ts` deliberately does not
 * re-read history at the start of a turn the tab did not begin. So the tab
 * that made the late approve re-reads that chat's history a few times itself.
 * When a re-read brings back the continuation's user row plus the assistant
 * row still marked `streamLive`, `useAgentChat`'s existing history effect
 * seeds it and the resume pursuit (FRONT-2195) re-attaches to the stream.
 * Nothing here opens a stream or sends a turn.
 *
 * The server waits for any live turn in the chat to end before it starts its
 * continuation, which can be far later than the ~20 s the schedule covers. So
 * a pickup stays ARMED for the chat until a continuation row is seen or 45
 * minutes pass, and the chat hook calls `rerunPickupOnTurnEnd` whenever a live
 * turn in that chat ends in this tab.
 */

/** Offsets (ms after the outcome) of each history re-read; the last is ~20s. */
export const CONTINUATION_REREAD_DELAYS_MS: readonly number[] = [1500, 4000, 8000, 14000, 20000];

/** A late approval is the deferred row; a live approve is continued by its own blocked turn. */
export function isLateApproval(approval: { waitingMode?: 'live' | 'deferred' }): boolean {
  return approval.waitingMode === 'deferred';
}

/** Which approve answers mean the late step has settled and the server will continue. */
export function shouldPickUpAfterApprove(
  approval: { waitingMode?: 'live' | 'deferred' },
  result: ApproveResponse | undefined,
): boolean {
  const e = result?.executed;
  return isLateApproval(approval) && (e === 'succeeded' || e === 'failed' || e === 'refused');
}

/** How many continuation rows a history snapshot holds (mapped by ACTION, never label). */
export function countContinuationRows(messages: readonly UIMessage[]): number {
  let n = 0;
  for (const m of messages) {
    if (m.role === 'user' && readSourceMeta(m)?.action === LATE_APPROVAL_CONTINUATION) n += 1;
  }
  return n;
}

/** Pure: re-read again only while no NEW continuation row has appeared. */
export function shouldRereadHistory(baselineRows: number, currentRows: number): boolean {
  return currentRows <= baselineRows;
}

function rowsInCache(queryClient: QueryClient, sessionId: string): number {
  let max = 0;
  const prefix = [...chatKeys.all, 'history', sessionId];
  for (const [, data] of queryClient.getQueriesData<UIMessage[]>({ queryKey: prefix })) {
    if (Array.isArray(data)) max = Math.max(max, countContinuationRows(data));
  }
  return max;
}

export interface PickupDeps {
  queryClient: Pick<QueryClient, 'getQueriesData' | 'invalidateQueries'>;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  delays?: readonly number[];
  /** Clock for the arming window; tests pass a fake one. */
  now?: () => number;
}

/**
 * How long a pickup stays armed. The server starts its continuation only once
 * any live turn in the chat has ended, and waits up to 40 minutes for that, so
 * the window outlasts it (45 minutes).
 */
export const PICKUP_ARMED_MS = 45 * 60 * 1000;

interface ArmedPickup {
  armedAt: number;
  /** Continuation rows the chat held when the late approve settled. */
  baseline: number;
  /** The currently running re-read schedule, cancelled before a new one starts. */
  cancel: () => void;
}

/**
 * Chats with a pickup armed: a late approve from THIS tab settled and no
 * continuation row has been seen since. The schedule above covers ~20 s; this
 * is what lets a turn that was still running in the chat (so the server's
 * continuation has not even started) be picked up when it ends.
 */
const armedPickups = new Map<string, ArmedPickup>();

/** Pure: is an armed pickup still worth re-running (no continuation seen, inside the window)? */
export function isPickupStillArmed(
  armed: Pick<ArmedPickup, 'armedAt' | 'baseline'> | undefined,
  currentRows: number,
  now: number,
): boolean {
  if (!armed) return false;
  if (now - armed.armedAt > PICKUP_ARMED_MS) return false;
  return shouldRereadHistory(armed.baseline, currentRows);
}

function runSchedule(sessionId: string, deps: PickupDeps, baseline: number, armedAt: number): () => void {
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  const delays = deps.delays ?? CONTINUATION_REREAD_DELAYS_MS;
  const qc = deps.queryClient as QueryClient;
  const handles: unknown[] = [];
  let stopped = false;
  const cancel = () => {
    stopped = true;
    for (const h of handles) clearTimer(h);
  };
  armedPickups.get(sessionId)?.cancel();
  const entry: ArmedPickup = { armedAt, baseline, cancel };
  armedPickups.set(sessionId, entry);
  for (const ms of delays) {
    handles.push(
      setTimer(() => {
        if (stopped) return;
        if (!shouldRereadHistory(baseline, rowsInCache(qc, sessionId))) {
          // A continuation row is in: nothing left to pick up.
          cancel();
          if (armedPickups.get(sessionId) === entry) armedPickups.delete(sessionId);
          return;
        }
        void qc.invalidateQueries({ queryKey: [...chatKeys.all, 'history', sessionId] });
      }, ms),
    );
  }
  return cancel;
}

/**
 * Schedule the history re-reads for one chat and ARM the pickup for it. Each
 * tick first looks at what the previous re-read left in the cache and cancels
 * the rest (and disarms) once a new continuation row is there. Returns a
 * cancel function for this schedule.
 */
export function scheduleContinuationPickup(sessionId: string, deps: PickupDeps): () => void {
  const qc = deps.queryClient as QueryClient;
  const now = (deps.now ?? Date.now)();
  return runSchedule(sessionId, deps, rowsInCache(qc, sessionId), now);
}

/**
 * A live turn in `sessionId` just ENDED in this tab. If a pickup is armed for
 * it (and still inside 45 minutes with no continuation row seen), run the
 * re-read schedule again from now. Returns the new schedule's cancel, or null
 * when nothing was armed or the pickup is spent (a spent one is disarmed).
 */
export function rerunPickupOnTurnEnd(sessionId: string, deps: PickupDeps): (() => void) | null {
  const armed = armedPickups.get(sessionId);
  if (!armed) return null;
  const qc = deps.queryClient as QueryClient;
  const now = (deps.now ?? Date.now)();
  if (!isPickupStillArmed(armed, rowsInCache(qc, sessionId), now)) {
    armed.cancel();
    armedPickups.delete(sessionId);
    return null;
  }
  return runSchedule(sessionId, deps, armed.baseline, armed.armedAt);
}

/** Pure: did a live turn just end — it was live (streaming/submitted) and the chat status is now idle or errored? */
export function turnEndedHere(wasLive: boolean, status: string): boolean {
  return wasLive && status !== 'streaming' && status !== 'submitted';
}

/** Test seam: forget every armed pickup. */
export function clearArmedPickups(): void {
  for (const a of armedPickups.values()) a.cancel();
  armedPickups.clear();
}
